/**
 * Mijoz ↔ menejer yordam chati — hozircha localStorage + custom event.
 * Backend ulanganda shu interfeysni API bilan almashtirish oson.
 */

export type SupportAuthorRole = 'tenant_owner' | 'regional_manager' | 'general_manager' | 'platform_owner';

export interface SupportMessage {
  id: string;
  tenantId: string;
  authorUserId?: string;
  authorRole: SupportAuthorRole;
  authorName: string;
  authorRegionName?: string;
  targetScope?: 'broadcast' | 'cashier';
  targetCashierId?: string;
  text?: string;
  attachmentType?: 'image' | 'video' | 'audio' | 'file';
  attachmentUrl?: string;
  attachmentFilename?: string;
  createdAt: string;
  replyToId?: string;
  replyPreviewText?: string;
  replyAuthorName?: string;
}

const EVENT = 'support-chat-updated';

/** localStorage dagi thread kaliti — tenant_owner profilida tenant_id raqam/string aralash bo‘lishi mumkin */
export function supportChatTenantId(user: { tenantId?: string | number | null } | null | undefined): string {
  const raw = user?.tenantId;
  if (raw == null || raw === '') return '1';
  const s = String(raw).trim();
  return s || '1';
}

function threadKey(tenantId: string) {
  return `neduz_support_thread_${tenantId || 'default'}`;
}

function parseMessages(raw: string | null): SupportMessage[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    if (!Array.isArray(v)) return [];
    return v.filter(
      (m): m is SupportMessage =>
        m &&
        typeof m === 'object' &&
        typeof (m as SupportMessage).id === 'string' &&
        typeof (m as SupportMessage).tenantId === 'string' &&
        typeof (m as SupportMessage).authorRole === 'string' &&
        typeof (m as SupportMessage).authorName === 'string' &&
        typeof (m as SupportMessage).createdAt === 'string' &&
        ((m as SupportMessage).authorUserId === undefined || typeof (m as SupportMessage).authorUserId === 'string'),
    );
  } catch {
    return [];
  }
}

export function getSupportMessages(tenantId: string): SupportMessage[] {
  try {
    return parseMessages(window.localStorage?.getItem?.(threadKey(tenantId)) ?? null);
  } catch {
    return [];
  }
}

export function getLastSupportMessage(tenantId: string): SupportMessage | null {
  const list = getSupportMessages(tenantId);
  return list.length ? list[list.length - 1] : null;
}

export type AppendSupportMessageOptions = {
  tenantRegionId?: string;
  companyName?: string;
};

export type AppendSupportMessagePayload = Omit<SupportMessage, 'id' | 'createdAt' | 'tenantId'> & {
  id?: string;
  createdAt?: string;
};

export type AppendSupportMessageResult = {
  message: SupportMessage;
  /** false bo‘lsa, brauzer xotirasi to‘lgan yoki yozish taqiqlangan */
  persisted: boolean;
};

export function appendSupportMessage(
  tenantId: string,
  msg: AppendSupportMessagePayload,
  options?: AppendSupportMessageOptions,
): AppendSupportMessageResult {
  const full: SupportMessage = {
    id: msg.id ?? `m_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    tenantId,
    authorUserId: msg.authorUserId,
    authorRole: msg.authorRole,
    authorName: msg.authorName,
    authorRegionName: msg.authorRegionName,
    text: msg.text,
    attachmentType: msg.attachmentType,
    attachmentUrl: msg.attachmentUrl,
    attachmentFilename: msg.attachmentFilename,
    createdAt: msg.createdAt ?? new Date().toISOString(),
    replyToId: msg.replyToId,
    replyPreviewText: msg.replyPreviewText,
    replyAuthorName: msg.replyAuthorName,
  };
  let persisted = false;
  try {
    const ls = typeof window !== 'undefined' ? window.localStorage : null;
    if (!ls) throw new Error('no localStorage');
    const prev = getSupportMessages(tenantId);
    const next = [...prev, full];
    ls.setItem(threadKey(tenantId), JSON.stringify(next));
    persisted = true;
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { tenantId } }));
    void import('./chatPushNotifications').then((mod) => {
      mod.recordChatPushFromMessage(full, options);
    });
  } catch {
    persisted = false;
  }
  return { message: full, persisted };
}

export function subscribeSupportChat(tenantId: string, cb: () => void): () => void {
  const onEvt = (e: Event) => {
    const d = (e as CustomEvent<{ tenantId?: string }>).detail;
    if (d?.tenantId === tenantId) cb();
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === threadKey(tenantId)) cb();
  };
  window.addEventListener(EVENT, onEvt);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, onEvt);
    window.removeEventListener('storage', onStorage);
  };
}

/** Har qanday tenant threadi yangilanganda (menejerlar ro‘yxati preview uchun) */
export function subscribeAnySupportChat(cb: () => void): () => void {
  const onEvt = () => cb();
  const onStorage = (e: StorageEvent) => {
    if (e.key?.startsWith('neduz_support_thread_')) cb();
  };
  window.addEventListener(EVENT, onEvt);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, onEvt);
    window.removeEventListener('storage', onStorage);
  };
}
